import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import _ from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import aiAssistantServices from "app/services/ai-assistant-services";
import AssistantMarkdown from "./AssistantMarkdown";
import { draftActionLabel, draftIcon, openAssistantDraft } from "./assistantDrafts";

const orbPulse = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(92, 20, 155, 0.32), 0 18px 42px rgba(92, 20, 155, 0.22); }
    70% { box-shadow: 0 0 0 16px rgba(92, 20, 155, 0), 0 18px 42px rgba(92, 20, 155, 0.22); }
    100% { box-shadow: 0 0 0 0 rgba(92, 20, 155, 0), 0 18px 42px rgba(92, 20, 155, 0.22); }
`;

const thinkingDotPulse = keyframes`
    0%, 80%, 100% { transform: translateY(0) scale(0.78); opacity: 0.42; }
    40% { transform: translateY(-3px) scale(1); opacity: 0.9; }
`;

const GENERAL_QUICK_ACTIONS = [
    "¿Cómo están mis equipos?",
    "¿Hubo alertas hoy?",
    "Reporte de temperaturas de ayer",
];

const ASSISTANT_PANEL_WIDTH = 430;
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 3 * 60 * 1000;
const ASSISTANT_GRADIENT = "linear-gradient(135deg, #5C149B 0%, #7B2CBF 48%, #E44A77 100%)";

const requestError = (error, fallback) => error?.response?.data?.errors?.[0] || error?.response?.data?.error || fallback;

// Xpit Assistant: el mismo asistente que responde por WhatsApp. Envía el mensaje con lo que muestra la página actual
// (context) y espera la respuesta consultando cada 2 segundos mientras haya una en camino, aunque se recargue la página.
// Los borradores se abren desde cualquier página (assistantDrafts).
const AssistantWidget = ({
    context = {},
    pageQuickActions = [],
    bottomOffset = 96,
    inline = false,
    open: controlledOpen,
    onOpenChange,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [internalOpen, setInternalOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [pending, setPending] = useState(false);
    const [applyingMessageId, setApplyingMessageId] = useState(null);
    const [input, setInput] = useState("");
    const [error, setError] = useState(null);
    const messagesEndRef = useRef(null);
    const optimisticMessageIdRef = useRef(0);
    const lastIdRef = useRef(null);

    const quickActions = _.uniq([...GENERAL_QUICK_ACTIONS, ...pageQuickActions]);
    const isOpenControlled = controlledOpen !== undefined;
    const open = isOpenControlled ? controlledOpen : internalOpen;

    const setAssistantOpen = useCallback((nextOpen) => {
        if (!isOpenControlled) {
            setInternalOpen(nextOpen);
        }
        onOpenChange?.(nextOpen);
    }, [isOpenControlled, onOpenChange]);

    const sortedMessages = useMemo(() => _.orderBy(messages || [], ["created_at", "id"], ["asc", "asc"]), [messages]);

    const scrollToBottom = useCallback(() => {
        window.setTimeout(() => messagesEndRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" }), 60);
    }, []);

    // Agrega mensajes sin repetir y recuerda el último id guardado, para pedir solo lo nuevo.
    const mergeMessages = useCallback((incoming = []) => {
        setMessages((prev) => _.uniqBy([...prev, ...incoming], (message) => `${message.id}`));
        const ids = incoming.map((message) => message.id).filter(Number.isInteger);
        if (ids.length) lastIdRef.current = Math.max(lastIdRef.current || 0, ...ids);
    }, []);

    const loadAssistant = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await aiAssistantServices.getConversation();
            mergeMessages(data?.messages || []);
            setPending(!!data?.pending);
            setLoaded(true);
            scrollToBottom();
        } catch (err) {
            setError(requestError(err, "No fue posible cargar Xpit Assistant."));
        } finally {
            setLoading(false);
        }
    }, [mergeMessages, scrollToBottom]);

    useEffect(() => {
        if (open && !loaded && !loading) loadAssistant();
    }, [loadAssistant, loaded, loading, open]);

    // Mientras haya una respuesta en camino, se consulta lo nuevo cada 2 segundos (hasta 3 minutos).
    useEffect(() => {
        if (!pending) return undefined;

        let cancelled = false;
        let timer = null;
        const startedAt = Date.now();
        const poll = async () => {
            try {
                const data = await aiAssistantServices.getConversation(lastIdRef.current);
                if (cancelled) return;
                mergeMessages(data?.messages || []);
                if (!data?.pending) {
                    setPending(false);
                    return;
                }
            } catch (_err) {}
            if (cancelled) return;
            if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
                setPending(false);
                setError("La respuesta está tardando más de lo normal. Vuelve a abrir el asistente en un rato.");
                return;
            }
            timer = window.setTimeout(poll, POLL_INTERVAL_MS);
        };
        timer = window.setTimeout(poll, POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [mergeMessages, pending]);

    useEffect(() => {
        if (open) scrollToBottom();
    }, [open, scrollToBottom, sortedMessages.length, sending, pending]);

    const sendPrompt = async (promptText = input) => {
        const content = `${promptText || ""}`.trim();
        if (!content || sending || pending) return;

        setSending(true);
        setError(null);
        setInput("");

        optimisticMessageIdRef.current += 1;
        const optimisticMessage = {
            id: `optimistic-${Date.now()}-${optimisticMessageIdRef.current}`,
            role: "user",
            content,
            created_at: new Date().toISOString(),
            draft_status: "none",
        };
        setMessages((prev) => [...prev, optimisticMessage]);
        scrollToBottom();

        try {
            const response = await aiAssistantServices.sendMessage(content, context);
            setMessages((prev) => prev.filter((message) => message.id !== optimisticMessage.id));
            mergeMessages([response?.message].filter(Boolean));
            setPending(true);
        } catch (err) {
            setMessages((prev) => prev.filter((message) => message.id !== optimisticMessage.id));
            setError(requestError(err, "No pude enviar tu mensaje. Intenta de nuevo."));
            setInput(content);
        } finally {
            setSending(false);
        }
    };

    const updateMessageInState = (updatedMessage) => {
        if (!updatedMessage?.id) return;
        setMessages((prev) => prev.map((message) => (
            Number(message.id) === Number(updatedMessage.id) ? updatedMessage : message
        )));
    };

    const discardDraft = async (message) => {
        if (!message?.id) return;
        try {
            const updated = await aiAssistantServices.updateDraftStatus(message.id, "discarded");
            updateMessageInState(updated);
        } catch (_err) {}
    };

    const renderThinkingBubble = () => (
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <Paper
                elevation={0}
                sx={{
                    px: 1.55,
                    py: 1.15,
                    borderRadius: "18px 18px 18px 4px",
                    bgcolor: "#FFFFFF",
                    border: `1px solid ${alpha("#8B7AA8", 0.16)}`,
                    boxShadow: `0 10px 22px ${alpha("#0F172A", 0.045)}`,
                }}>
                <Stack direction="row" spacing={0.65} alignItems="center">
                    {[0, 1, 2].map((index) => (
                        <Box
                            key={`assistant-thinking-dot-${index}`}
                            sx={{
                                width: 7,
                                height: 7,
                                borderRadius: "50%",
                                bgcolor: alpha("#7B6A96", 0.52),
                                animation: `${thinkingDotPulse} 1.25s ease-in-out infinite`,
                                animationDelay: `${index * 0.16}s`,
                            }}
                        />
                    ))}
                </Stack>
            </Paper>
        </Box>
    );

    // Abre el borrador en su vista (que no guarda nada hasta que el usuario lo hace) y lo marca como aplicado. En el
    // celular el panel tapa la vista, así que se cierra.
    const applyDraft = async (message) => {
        if (!message?.id) return;

        setApplyingMessageId(message.id);
        setError(null);
        try {
            openAssistantDraft(message, navigate, location);
            const updated = await aiAssistantServices.updateDraftStatus(message.id, "accepted");
            updateMessageInState(updated);
            if (isMobile) setAssistantOpen(false);
        } catch (_err) {
            setError("No pude abrir el borrador.");
        } finally {
            setApplyingMessageId(null);
        }
    };

    const renderDraftIcon = (draftType) => {
        const Icon = draftIcon(draftType);
        return <Icon fontSize="small" />;
    };

    const renderDraftCard = (message) => {
        const summary = message?.draft_summary || {};
        const changes = Array.isArray(summary.changes) ? summary.changes : [];
        const executionPlan = Array.isArray(summary.execution_plan) ? summary.execution_plan : [];
        const isProposed = message?.draft_status === "proposed";
        const isAccepted = message?.draft_status === "accepted";
        const isDiscarded = message?.draft_status === "discarded";
        const applying = applyingMessageId === message.id;

        if (!message?.draft_type || message?.draft_status === "none") return null;

        return (
            <Paper
                variant="outlined"
                sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: 3,
                    borderColor: alpha("#5C149B", 0.22),
                    background: `linear-gradient(135deg, ${alpha("#5C149B", 0.07)} 0%, rgba(255,255,255,0.98) 72%)`,
                }}>
                <Stack spacing={1.25}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Avatar
                            sx={{
                                width: 34,
                                height: 34,
                                bgcolor: alpha("#5C149B", 0.12),
                                color: "#5C149B",
                            }}>
                            {renderDraftIcon(message.draft_type)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {summary.title || "Borrador listo para revisar"}
                            </Typography>
                            {!!summary.impact && (
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                                    {summary.impact}
                                </Typography>
                            )}
                        </Box>
                    </Stack>

                    {!!changes.length && (
                        <Stack spacing={0.5}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7 }}>
                                Cambios propuestos
                            </Typography>
                            {changes.slice(0, 4).map((change, index) => (
                                <Stack key={`assistant-change-${message.id}-${index}`} direction="row" spacing={0.75} alignItems="flex-start">
                                    <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 16, color: "#5C149B", mt: "2px" }} />
                                    <Typography variant="body2">{change}</Typography>
                                </Stack>
                            ))}
                        </Stack>
                    )}

                    {!!executionPlan.length && (
                        <Box
                            sx={{
                                p: 1,
                                borderRadius: 2,
                                bgcolor: alpha("#0F172A", 0.035),
                            }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                Plan de ejecución
                            </Typography>
                            {executionPlan.slice(0, 3).map((step, index) => (
                                <Typography key={`assistant-plan-${message.id}-${index}`} variant="body2" color="text.secondary">
                                    {index + 1}. {step}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Chip
                            size="small"
                            label={isAccepted ? "Aplicado" : isDiscarded ? "Descartado" : "Borrador editable"}
                            color={isAccepted ? "success" : isDiscarded ? "default" : "primary"}
                            variant={isAccepted ? "filled" : "outlined"}
                            sx={{ borderRadius: 999 }}
                        />
                        {isProposed && (
                            <Stack direction="row" spacing={0.75}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={applying ? <CircularProgress color="inherit" size={14} /> : <EditOutlinedIcon />}
                                    disabled={applying}
                                    onClick={() => applyDraft(message)}
                                    sx={{
                                        borderRadius: 999,
                                        textTransform: "none",
                                        boxShadow: `0 10px 22px ${alpha("#5C149B", 0.22)}`,
                                    }}>
                                    {draftActionLabel(message.draft_type)}
                                </Button>
                                <IconButton size="small" color="error" onClick={() => discardDraft(message)} disabled={applying}>
                                    <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            </Paper>
        );
    };

    const renderMessage = (message) => {
        const isUser = message.role === "user";

        return (
            <Box
                key={`assistant-message-${message.id}`}
                sx={{
                    display: "flex",
                    justifyContent: isUser ? "flex-end" : "flex-start",
                }}>
                <Box sx={{ maxWidth: isUser ? "88%" : "94%", minWidth: 0 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            px: 1.35,
                            py: 1,
                            borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                            bgcolor: isUser ? "#5C149B" : "#FFFFFF",
                            color: isUser ? "#FFFFFF" : "text.primary",
                            border: isUser ? "none" : `1px solid ${alpha("#0F172A", 0.08)}`,
                        }}>
                        {isUser ? (
                            <Typography variant="body2" sx={{ whiteSpace: "pre-line", lineHeight: 1.45, overflowWrap: "anywhere" }}>
                                {message.content}
                            </Typography>
                        ) : (
                            <AssistantMarkdown>{message.content}</AssistantMarkdown>
                        )}
                    </Paper>
                    {!isUser && renderDraftCard(message)}
                </Box>
            </Box>
        );
    };

    const panelSx = inline
        ? {
            // Sticky (no relative): el panel acompaña el scroll de la vista en vez de quedarse arriba.
            position: { xs: "fixed", md: "sticky" },
            top: 0,
            right: { xs: 0, md: "auto" },
            bottom: { xs: 0, md: "auto" },
            flex: { md: `0 0 ${ASSISTANT_PANEL_WIDTH}px` },
            width: { xs: "100%", md: ASSISTANT_PANEL_WIDTH },
            minWidth: { md: ASSISTANT_PANEL_WIDTH },
            maxWidth: "100vw",
            height: "100vh",
            // sticky no funciona con alignSelf stretch: el elemento tiene que poder moverse dentro del contenedor.
            alignSelf: { xs: "stretch", md: "flex-start" },
            zIndex: { xs: 1302, md: 1 },
        }
        : {
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            width: { xs: "100%", sm: 420, md: 430 },
            maxWidth: "100vw",
            zIndex: (theme) => theme.zIndex.drawer + 2,
        };

    return (
        <>
            {!open && (
                <Tooltip title="Pregúntale a Xpit Assistant">
                    <Button
                        onClick={() => setAssistantOpen(true)}
                        sx={{
                            position: "fixed",
                            right: { xs: 18, md: 28 },
                            bottom: { xs: bottomOffset, md: bottomOffset },
                            zIndex: (theme) => theme.zIndex.drawer + 1,
                            minWidth: 0,
                            width: 60,
                            height: 60,
                            borderRadius: "50%",
                            color: "#FFFFFF",
                            background: ASSISTANT_GRADIENT,
                            animation: `${orbPulse} 2.8s ease-out infinite`,
                            "&:hover": {
                                background: "linear-gradient(135deg, #4A0F80 0%, #6E22B6 48%, #D83E6B 100%)",
                                transform: "translateY(-2px) scale(1.02)",
                            },
                        }}>
                        <AutoAwesomeOutlinedIcon sx={{ fontSize: 28 }} />
                    </Button>
                </Tooltip>
            )}

            {open && (
                <>
                    <Box
                        component="aside"
                        sx={{
                            ...panelSx,
                            overflow: "hidden",
                            background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
                            borderLeft: `1px solid ${alpha("#5C149B", 0.14)}`,
                            boxShadow: inline
                                ? "none"
                                : `-12px 0 34px ${alpha("#0F172A", 0.08)}`,
                        }}>
                        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    <Box
                        sx={{
                            px: 1.5,
                            py: 1.15,
                            color: "text.primary",
                            background: "linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(243,248,255,0.92) 100%)",
                            borderBottom: `1px solid ${alpha("#5C149B", 0.12)}`,
                        }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                <Avatar
                                    sx={{
                                        width: 34,
                                        height: 34,
                                        color: "#FFFFFF",
                                        background: ASSISTANT_GRADIENT,
                                    }}>
                                    <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="subtitle1" sx={{ lineHeight: 1.1, fontWeight: 700 }}>
                                        Xpit Assistant
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                        Tus equipos, alertas, reportes y dashboards
                                    </Typography>
                                </Box>
                            </Stack>
                            <IconButton size="small" onClick={() => setAssistantOpen(false)} sx={{ color: "text.secondary" }}>
                                <CloseOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Box>

                    {loading && <LinearProgress />}

                    <Box sx={{ p: 1.5 }}>
                        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
                            {quickActions.map((action) => (
                                <Chip
                                    key={`assistant-quick-${action}`}
                                    label={action}
                                    size="small"
                                    clickable
                                    onClick={() => sendPrompt(action)}
                                    disabled={sending || pending || loading}
                                    sx={{
                                        borderRadius: 999,
                                        bgcolor: alpha("#5C149B", 0.08),
                                        color: "#5C149B",
                                        border: `1px solid ${alpha("#5C149B", 0.18)}`,
                                        "&:hover": {
                                            bgcolor: alpha("#5C149B", 0.13),
                                        },
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>

                    <Divider />

                    <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, py: 2 }}>
                        <Stack spacing={1.4}>
                            {error && <Alert severity="error">{error}</Alert>}

                            {!loading && !sortedMessages.length && (
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        borderRadius: 3,
                                        borderColor: alpha("#5C149B", 0.16),
                                        background: `linear-gradient(135deg, ${alpha("#5C149B", 0.06)} 0%, rgba(255,255,255,0.96) 100%)`,
                                    }}>
                                    <Stack spacing={0.75}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            Hola, soy Xpit Assistant
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Pregúntame por tus equipos, lecturas, alertas o reportes. Si tu perfil puede editar, también te
                                            propongo dashboards y automatizaciones como borrador para que los revises.
                                        </Typography>
                                    </Stack>
                                </Paper>
                            )}

                            {sortedMessages.map(renderMessage)}

                            {(sending || pending) && renderThinkingBubble()}

                            <div ref={messagesEndRef} />
                        </Stack>
                    </Box>

                    <Divider />

                    <Box sx={{ p: 1.5, bgcolor: "#FFFFFF" }}>
                        <Stack direction="row" spacing={1} alignItems="flex-end">
                            <TextField
                                fullWidth
                                multiline
                                maxRows={4}
                                placeholder="Ej: ¿cuál es la temperatura actual de cada refrigerador?"
                                value={input}
                                disabled={sending || pending}
                                onChange={(event) => setInput(event.target.value)}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                    },
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" && !event.shiftKey) {
                                        event.preventDefault();
                                        sendPrompt();
                                    }
                                }}
                            />
                            <IconButton
                                color="primary"
                                disabled={sending || pending || !input.trim()}
                                onClick={() => sendPrompt()}
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 2,
                                    bgcolor: alpha("#5C149B", 0.1),
                                }}>
                                <SendRoundedIcon />
                            </IconButton>
                        </Stack>
                    </Box>
                        </Box>
                    </Box>
                </>
            )}
        </>
    );
};

export default AssistantWidget;
